import { Box, Button, Card, CardBody, CardHeader, CheckBox, Heading, Image, Markdown, Text, TextInput } from "grommet";
import { Checkmark, Copy, Update, Upload } from "grommet-icons";
import { useTranslation } from "react-i18next";
import { useUpdatePaymentOptionMutation } from "../../api/paymentOptionsApi";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { addNotification } from "../../slices/notificationsSlice";
import { InfoSection } from "../layout/InfoSection";
import { TextInputWithButton } from "../../forms/TextInputWithButton";
import { DonatikTipsTranslation } from "../../md-translation/DonatikTipsTranslation";
import { FadedAccordion } from "../layout/FadedAccordion";

export const DonatikPaymentMethodCard = ({ enabled, apiKey, nickname, helpLink }) => {
    const { t, i18n } = useTranslation('donatik-settings');

    const dispatch = useDispatch();

    const [updatePaymentOption] = useUpdatePaymentOptionMutation();

    const [apiKeyValue, setApiKeyValue] = useState(apiKey);
    const [callbackUrl, setCallbackUrl] = useState(`${process.env.REACT_APP_API_BASE_URL}/payments/donatik/callback`);
    const [callbackCopied, setCallbackCopied] = useState(false);

    const [donatePageName, setDonatePageName] = useState(nickname);

    const handleEnabledToggle = (value) => {
        updatePaymentOption(value)
        .unwrap()
        .then(response => dispatch(addNotification({ show: true, message: `Payment option ${value.enabled ? 'enabled' : 'disabled'}.`, status: 'normal' })))
        .catch(error => dispatch(addNotification({ show: true, message: `Unable to ${value.enabled ? 'enabled' : 'disabled'} payment option.`, status: 'critical' }))) 
    }

    const handleApiKeyUpdate = () => {
        updatePaymentOption({ type: 'DONATIK', donatikApiKey: apiKeyValue })
        .unwrap()
        .then(response => dispatch(addNotification({ show: true, message: 'Api key successfully saved.', status: 'normal' })))
        .catch(error => dispatch(addNotification({ show: true, message: 'Unable to save new api key.', status: 'critical' })))
    }

    const handleCallbackCopy = () => {
        navigator.clipboard.writeText(apiKey ? `${callbackUrl}/${apiKeyValue}` : '')
        setCallbackCopied(true);
        dispatch(addNotification({ show: true, message: 'Callback url successfully copied.', status: 'normal' }))
        setTimeout(() => {
            setCallbackCopied(false);
        }, 1000)
    }

    const handleDonatePageName = () => {
        updatePaymentOption({ type: 'DONATIK', nickname: donatePageName })
        .unwrap()
        .then(response => dispatch(addNotification({ show: true, message: `Donate page name successfully saved.`, status: 'normal' })))
        .catch(error => dispatch(addNotification({ show: true, message: `Unable to save donate page name.`, status: 'critical' })))
    }

    return (
        <Card>
            <CardHeader pad={{ top: 'medium', left: 'medium', right: 'medium' }}>
                <Box direction="row" gap="small">
                    <Image height={'25px'} width={'115px'} src={`${process.env.PUBLIC_URL}/images/donatik-logo.svg`} /><Heading margin={{top: 'none', bottom: 'none'}} level={2} size="medium" weight={'bold'}>{t('title')}</Heading>
                </Box>
                <CheckBox toggle pad={'none'} checked={enabled} onClick={(event) => handleEnabledToggle({ type: 'DONATIK', enabled: event.target.checked })} />
            </CardHeader>
            <CardBody direction="row-responsive" gap="medium" responsive>
                <Box basis="50%" border={{ color: 'background-contrast', side: 'top', size: 'small' }}>
                    <Box margin={{ top: 'medium', bottom: 'small' }}>
                        <Box direction="row" gap="xsmall">
                            <Text>{t('step1')}</Text>
                            <Button href={helpLink} target="_blank" style={{textDecoration: 'underline'}}>
                                {t('step1-link')}
                            </Button>
                        </Box>
                        <Markdown>
                            {t('step2')}
                        </Markdown>
                        <Markdown>
                            {t('step3')}
                        </Markdown>
                        <Markdown>
                            {t('step4')}
                        </Markdown>
                    </Box>
                    <TextInputWithButton type="password" value={apiKeyValue} onChange={(event) => setApiKeyValue(event.target.value)} width={'medium'}>
                        <Button style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }} icon={<Upload />} onClick={handleApiKeyUpdate} />
                    </TextInputWithButton>
                    {/* <Box direction="row" border round="xsmall" width={'medium'}>
                        <TextInput plain type="password" value={apiKeyValue} onChange={(event) => setApiKeyValue(event.target.value)}/>
                        <Box flex="false">
                            <Button icon={<Upload />} onClick={handleApiKeyUpdate} />
                        </Box>
                    </Box> */}
                    <Box margin={{top: 'small', bottom: 'small'}}>
                        <Markdown>
                            {t('step5')}
                        </Markdown>
                    </Box>
                    <TextInputWithButton type="text" placeholder={t('callbackUrlPlaceholder')} value={apiKey ? `${callbackUrl}/${apiKeyValue}` : ''} readOnly width={'medium'}>
                        <Button style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }} icon={callbackCopied ? <Checkmark/> : <Copy/>} onClick={handleCallbackCopy}/>
                    </TextInputWithButton>
                    {/* <Box direction="row" border round="xsmall" width={'medium'}>
                        <TextInput plain type="text" placeholder={t('callbackUrlPlaceholder')} value={apiKey ? `${callbackUrl}/${apiKeyValue}` : ''} readOnly/>
                        <Button icon={callbackCopied ? <Checkmark/> : <Copy/>} onClick={handleCallbackCopy}/>
                    </Box> */}
                    <Box margin={{top: 'small', bottom: 'small'}}>
                        <Markdown>
                            {t('step6')}
                        </Markdown>
                    </Box>
                    <TextInputWithButton type="text" placeholder="Enter nickname here" value={donatePageName} onChange={(event) => setDonatePageName(event.target.value)} width={'medium'}>
                        <Button style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }} icon={<Upload />} onClick={handleDonatePageName}/>
                    </TextInputWithButton>
                    {/* <Box direction="row" border round="xsmall" width={'medium'}>
                        <TextInput plain type="text" placeholder="Enter nickname here" value={donatePageName} onChange={(event) => setDonatePageName(event.target.value)}/>
                        <Button icon={<Upload />} onClick={handleDonatePageName}/>
                    </Box> */}
                </Box>
                <Box basis="50%">
                    <FadedAccordion previewHeight={300}>
                        <InfoSection>
                            <Box>
                                <Markdown components={{ p: { props: { style: { maxWidth: '100%' } } }, img: { props: { style: { width: '100%' } } } }}>{DonatikTipsTranslation[i18n.language]}</Markdown>
                            </Box>
                        </InfoSection>
                    </FadedAccordion>
                </Box>
            </CardBody>
        </Card>
    )
}